/**
 * UGC / BuzzBridge campaign budget floor (creators + platform fees only).
 * Fee rates come from global configuration (`influencer_tiers` + `buzzbridge.ugc_budget`).
 */

import { DEFAULT_INFLUENCER_TIERS, DEFAULT_UGC_BUDGET } from './ugcCampaignConfigDefaults';
import type { IInfluencerTierConfig, IUgcCampaignFeeConfig } from '../types/global/ugcCampaignConfig.types';
import {
    buildUgcCampaignFeeConfig,
    findTierForBreakdown,
    getEffectiveMinPayoutPerCreator,
} from '../utils/ugcCampaignConfig';

/** @deprecated Use `ugcBudget.absolute_min_total_budget_usd` from global config. Kept for legacy imports. */
export const ABSOLUTE_MIN_TOTAL_BUDGET_USD = DEFAULT_UGC_BUDGET.absolute_min_total_budget_usd;

export type CampaignInfluencerTierId = 'micro' | 'mid' | 'macro' | 'top-tier';

export interface CampaignFeeBreakdownTierDisplay {
    id: string;
    name: string;
    reach: string;
    platformFee: number;
    minPayout?: number;
    description?: string;
}

/** Fallback tier cards when global config is not loaded (offsite creator). */
export const OFFSITE_INFLUENCER_TIER_CARDS: ReadonlyArray<CampaignFeeBreakdownTierDisplay> =
    DEFAULT_INFLUENCER_TIERS.map(tierToDisplay);

export interface CampaignFeeBreakdownInput {
    platforms: string[];
    participantsCount: number;
    influencerTier: string;
    totalBudget: number;
    /** Uses min_payout_per_creator_usd_subscribed when configured. */
    hostHasActiveSubscription?: boolean;
    /** For recurrent deliverable campaigns: multiplies creator pay by this many deliveries. Defaults to 1. */
    deliveryRoundsCount?: number;
}

export interface CampaignFeeBreakdownResult {
    tier: CampaignFeeBreakdownTierDisplay;
    participantsUsed: number;
    deliveryRoundsUsed: number;
    minimumPerParticipant: number;
    minimumParticipantPay: number;
    actualPerParticipant: number;
    platformFeePerPlatform: number;
    totalPlatformFee: number;
    /** Unsubscribed host min payout when tier has a lower subscribed rate. */
    minPayoutPerCreatorUnsubscribed?: number;
    minPayoutPerCreatorSubscribed?: number;
    minimumParticipantPayIfSubscribed?: number;
    hostMinPayoutSubscribeToSavePerCreatorUsd?: number | null;
    platformFeePerParticipant: number;
    subtotal: number;
    total: number;
    calculatedMinimumTotal: number;
    minimumRequiredBudget: number;
    policyFloorApplied: boolean;
    suggestedBudgetComfort: number;
    suggestedBudgetStrong: number;
}

function tierToDisplay(tier: IInfluencerTierConfig): CampaignFeeBreakdownTierDisplay {
    return {
        id: tier.id,
        name: tier.name,
        reach: tier.reach,
        platformFee: tier.platform_fee_per_platform_usd,
        minPayout: tier.min_payout_per_creator_usd,
        description: tier.description,
    };
}

/**
 * Same total budget → how many more creator slots at subscribed min payout vs unsubscribed.
 * Returns null when subscribed tier pricing is unavailable or not cheaper.
 */
export function computeExtraCreatorsAtSubscribedRate(input: {
    totalBudgetUsd: number;
    unsubscribedMinPerCreatorUsd: number;
    subscribedMinPerCreatorUsd?: number | null;
    platformFeesUsd: number;
}): number | null {
    const subscribed = input.subscribedMinPerCreatorUsd;
    const unsubscribed = input.unsubscribedMinPerCreatorUsd;
    if (subscribed == null || subscribed <= 0 || unsubscribed <= subscribed) return null;

    const creatorPoolUsd = input.totalBudgetUsd - input.platformFeesUsd;
    if (creatorPoolUsd <= 0) return null;

    const unsubscribedCount = Math.floor(creatorPoolUsd / unsubscribed);
    const subscribedCount = Math.floor(creatorPoolUsd / subscribed);
    const extra = subscribedCount - unsubscribedCount;
    return extra > 0 ? extra : null;
}

/**
 * Computes minimum required budget and per-participant allocation from total budget.
 * Pass `config` from Redux (`ugcCampaignFeeConfigSelector`) when available.
 */
export function computeCampaignFeeBreakdown(
    input: CampaignFeeBreakdownInput,
    config?: Partial<IUgcCampaignFeeConfig>,
): CampaignFeeBreakdownResult {
    const { tiers, ugcBudget } = buildUgcCampaignFeeConfig(config?.tiers, config?.ugcBudget, config?.creatorPayout);
    const tier = findTierForBreakdown(tiers, input.influencerTier || 'micro');

    const selectedPlatforms = input.platforms || [];
    const rawCount = Number(input.participantsCount);
    const participants = Math.max(1, Number.isFinite(rawCount) && rawCount > 0 ? Math.floor(rawCount) : 1);
    const rawRounds = Number(input.deliveryRoundsCount);
    const deliveryRounds = Number.isFinite(rawRounds) && rawRounds > 1 ? Math.floor(rawRounds) : 1;
    const totalBudget = Number(input.totalBudget) || 0;

    const hostHasSub = input.hostHasActiveSubscription === true;
    const unsubscribedMinPayout = tier.min_payout_per_creator_usd;
    const subscribedMinPayout =
        typeof tier.min_payout_per_creator_usd_subscribed === 'number'
            ? tier.min_payout_per_creator_usd_subscribed
            : null;
    const minimumPerParticipant = getEffectiveMinPayoutPerCreator(tier, hostHasSub);
    const platformFeePerPlatform = tier.platform_fee_per_platform_usd;
    const platformCount = selectedPlatforms.length;
    const totalPlatformFee = platformCount > 0 ? platformFeePerPlatform * platformCount : 0;
    const hostMinPayoutSubscribeToSavePerCreatorUsd =
        subscribedMinPayout != null && unsubscribedMinPayout > subscribedMinPayout
            ? Math.round((unsubscribedMinPayout - subscribedMinPayout) * 100) / 100
            : null;
    const platformFeePerParticipant = totalPlatformFee / participants;

    // For recurrent campaigns, creator pay scales by number of deliveries
    const minimumParticipantPay = minimumPerParticipant * participants * deliveryRounds;
    const minimumParticipantPayIfSubscribed =
        subscribedMinPayout != null ? subscribedMinPayout * participants * deliveryRounds : undefined;
    const minimumSubtotal = minimumParticipantPay + totalPlatformFee;
    const calculatedMinimumTotal = minimumSubtotal;
    const minimumRequiredBudget = Math.max(ugcBudget.absolute_min_total_budget_usd, calculatedMinimumTotal);
    const policyFloorApplied = minimumRequiredBudget > calculatedMinimumTotal + 0.005;

    // actualPerParticipant = pay per creator per delivery
    let actualPerParticipant: number = minimumPerParticipant;
    let actualSubtotal = minimumSubtotal;
    let total = calculatedMinimumTotal;

    const budgetMeetsPolicy = totalBudget + 0.005 >= minimumRequiredBudget;

    if (budgetMeetsPolicy && totalBudget > 0) {
        const maxParticipantPay = totalBudget - totalPlatformFee;
        const maxPerParticipant = maxParticipantPay / participants / deliveryRounds;

        if (maxPerParticipant >= minimumPerParticipant) {
            actualPerParticipant = maxPerParticipant;
            actualSubtotal = actualPerParticipant * participants * deliveryRounds + totalPlatformFee;
            total = actualSubtotal;
        }
    }

    const suggestedBudgetComfort = Math.ceil(minimumRequiredBudget * ugcBudget.suggested_budget_multiplier_comfort);
    const suggestedBudgetStrong = Math.ceil(minimumRequiredBudget * ugcBudget.suggested_budget_multiplier_strong);

    return {
        tier: tierToDisplay(tier),
        participantsUsed: participants,
        deliveryRoundsUsed: deliveryRounds,
        minimumPerParticipant,
        minimumParticipantPay,
        actualPerParticipant,
        platformFeePerPlatform,
        totalPlatformFee,
        minPayoutPerCreatorUnsubscribed: subscribedMinPayout != null ? unsubscribedMinPayout : undefined,
        minPayoutPerCreatorSubscribed: subscribedMinPayout ?? undefined,
        minimumParticipantPayIfSubscribed,
        hostMinPayoutSubscribeToSavePerCreatorUsd,
        platformFeePerParticipant,
        subtotal: actualSubtotal,
        total,
        calculatedMinimumTotal,
        minimumRequiredBudget,
        policyFloorApplied,
        suggestedBudgetComfort,
        suggestedBudgetStrong,
    };
}
